import { createServerFn } from "@tanstack/react-start";
import { and, desc, eq } from "drizzle-orm";
import { z } from "zod";
import { getDb, schema } from "../db/client.server";
import { requirePermission } from "../auth/session.server";
import {
  getDefaultLegalDocument,
  type LegalDocumentSlug,
  legalDocumentLabels,
} from "../legal/defaults";

const slugSchema = z.enum(["privacy", "terms"]);

const documentInput = z.object({
  slug: slugSchema,
  title: z.string().min(1),
  draftContentHtml: z.string(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
});

function mapPublished(row: typeof schema.legalDocuments.$inferSelect | undefined, slug: LegalDocumentSlug) {
  const defaults = getDefaultLegalDocument(slug);
  if (!row?.publishedContentHtml) {
    return {
      slug,
      title: defaults.title,
      contentHtml: defaults.contentHtml,
      metaTitle: defaults.metaTitle,
      metaDescription: defaults.metaDescription,
      currentVersion: 0,
      lastPublishedAt: null as Date | null,
    };
  }
  return {
    slug,
    title: row.title,
    contentHtml: row.publishedContentHtml,
    metaTitle: row.metaTitle ?? defaults.metaTitle,
    metaDescription: row.metaDescription ?? defaults.metaDescription,
    currentVersion: row.currentVersion,
    lastPublishedAt: row.lastPublishedAt,
  };
}

export const getPublishedLegalDocument = createServerFn({ method: "GET" })
  .inputValidator(z.object({ slug: slugSchema }))
  .handler(async ({ data }) => {
    const [row] = await getDb()
      .select()
      .from(schema.legalDocuments)
      .where(eq(schema.legalDocuments.slug, data.slug))
      .limit(1);
    return mapPublished(row, data.slug);
  });

export const listLegalDocumentsAdmin = createServerFn({ method: "GET" }).handler(async () => {
  await requirePermission("pages", "read");
  const rows = await getDb().select().from(schema.legalDocuments);
  const bySlug = new Map(rows.map((row) => [row.slug, row]));

  return (["privacy", "terms"] as const).map((slug) => {
    const row = bySlug.get(slug);
    const defaults = getDefaultLegalDocument(slug);
    return {
      slug,
      label: legalDocumentLabels[slug],
      title: row?.title ?? defaults.title,
      currentVersion: row?.currentVersion ?? 0,
      lastPublishedAt: row?.lastPublishedAt ?? null,
      updatedAt: row?.updatedAt ?? null,
      hasDraftChanges:
        !!row &&
        row.draftContentHtml !== null &&
        row.draftContentHtml !== row.publishedContentHtml,
    };
  });
});

export const getLegalDocumentAdmin = createServerFn({ method: "GET" })
  .inputValidator(z.object({ slug: slugSchema }))
  .handler(async ({ data }) => {
    await requirePermission("pages", "read");
    const [row] = await getDb()
      .select()
      .from(schema.legalDocuments)
      .where(eq(schema.legalDocuments.slug, data.slug))
      .limit(1);
    const defaults = getDefaultLegalDocument(data.slug);
    if (!row) {
      return {
        slug: data.slug,
        title: defaults.title,
        draftContentHtml: defaults.contentHtml,
        publishedContentHtml: defaults.contentHtml,
        metaTitle: defaults.metaTitle,
        metaDescription: defaults.metaDescription,
        currentVersion: 0,
        lastPublishedAt: null as Date | null,
        isSeeded: false,
      };
    }
    return {
      slug: row.slug,
      title: row.title,
      draftContentHtml: row.draftContentHtml ?? row.publishedContentHtml ?? defaults.contentHtml,
      publishedContentHtml: row.publishedContentHtml ?? defaults.contentHtml,
      metaTitle: row.metaTitle ?? defaults.metaTitle,
      metaDescription: row.metaDescription ?? defaults.metaDescription,
      currentVersion: row.currentVersion,
      lastPublishedAt: row.lastPublishedAt,
      isSeeded: true,
    };
  });

export const saveLegalDocumentDraft = createServerFn({ method: "POST" })
  .inputValidator(documentInput)
  .handler(async ({ data }) => {
    const user = await requirePermission("pages", "write");
    const db = getDb();
    const defaults = getDefaultLegalDocument(data.slug);
    const [existing] = await db
      .select()
      .from(schema.legalDocuments)
      .where(eq(schema.legalDocuments.slug, data.slug))
      .limit(1);

    if (!existing) {
      const [row] = await db
        .insert(schema.legalDocuments)
        .values({
          slug: data.slug,
          title: data.title,
          draftContentHtml: data.draftContentHtml,
          publishedContentHtml: null,
          metaTitle: data.metaTitle ?? defaults.metaTitle,
          metaDescription: data.metaDescription ?? defaults.metaDescription,
          currentVersion: 0,
          updatedBy: user.id,
        })
        .returning();
      return row;
    }

    const [row] = await db
      .update(schema.legalDocuments)
      .set({
        title: data.title,
        draftContentHtml: data.draftContentHtml,
        metaTitle: data.metaTitle ?? existing.metaTitle,
        metaDescription: data.metaDescription ?? existing.metaDescription,
        updatedBy: user.id,
        updatedAt: new Date(),
      })
      .where(eq(schema.legalDocuments.id, existing.id))
      .returning();
    return row;
  });

export const publishLegalDocument = createServerFn({ method: "POST" })
  .inputValidator(documentInput.extend({ changeNote: z.string().max(500).optional() }))
  .handler(async ({ data }) => {
    const user = await requirePermission("pages", "write");
    const db = getDb();
    const defaults = getDefaultLegalDocument(data.slug);

    let [doc] = await db
      .select()
      .from(schema.legalDocuments)
      .where(eq(schema.legalDocuments.slug, data.slug))
      .limit(1);

    if (!doc) {
      [doc] = await db
        .insert(schema.legalDocuments)
        .values({
          slug: data.slug,
          title: data.title,
          draftContentHtml: data.draftContentHtml,
          publishedContentHtml: data.draftContentHtml,
          metaTitle: data.metaTitle ?? defaults.metaTitle,
          metaDescription: data.metaDescription ?? defaults.metaDescription,
          currentVersion: 1,
          lastPublishedAt: new Date(),
          updatedBy: user.id,
        })
        .returning();
      await db.insert(schema.legalDocumentVersions).values({
        documentId: doc.id,
        version: 1,
        title: data.title,
        contentHtml: data.draftContentHtml,
        metaTitle: data.metaTitle ?? defaults.metaTitle,
        metaDescription: data.metaDescription ?? defaults.metaDescription,
        changeNote: data.changeNote ?? null,
        createdBy: user.id,
      });
      return { version: 1 };
    }

    const nextVersion = doc.currentVersion + 1;
    const [updated] = await db
      .update(schema.legalDocuments)
      .set({
        title: data.title,
        draftContentHtml: data.draftContentHtml,
        publishedContentHtml: data.draftContentHtml,
        metaTitle: data.metaTitle ?? doc.metaTitle,
        metaDescription: data.metaDescription ?? doc.metaDescription,
        currentVersion: nextVersion,
        lastPublishedAt: new Date(),
        updatedBy: user.id,
        updatedAt: new Date(),
      })
      .where(eq(schema.legalDocuments.id, doc.id))
      .returning();

    await db.insert(schema.legalDocumentVersions).values({
      documentId: updated.id,
      version: nextVersion,
      title: data.title,
      contentHtml: data.draftContentHtml,
      metaTitle: data.metaTitle ?? updated.metaTitle,
      metaDescription: data.metaDescription ?? updated.metaDescription,
      changeNote: data.changeNote ?? null,
      createdBy: user.id,
    });

    return { version: nextVersion };
  });

export const listLegalDocumentVersions = createServerFn({ method: "GET" })
  .inputValidator(z.object({ slug: slugSchema }))
  .handler(async ({ data }) => {
    await requirePermission("pages", "read");
    const [doc] = await getDb()
      .select({ id: schema.legalDocuments.id })
      .from(schema.legalDocuments)
      .where(eq(schema.legalDocuments.slug, data.slug))
      .limit(1);
    if (!doc) return [];

    return getDb()
      .select({
        id: schema.legalDocumentVersions.id,
        version: schema.legalDocumentVersions.version,
        title: schema.legalDocumentVersions.title,
        changeNote: schema.legalDocumentVersions.changeNote,
        publishedAt: schema.legalDocumentVersions.publishedAt,
        createdByName: schema.users.name,
      })
      .from(schema.legalDocumentVersions)
      .leftJoin(schema.users, eq(schema.legalDocumentVersions.createdBy, schema.users.id))
      .where(eq(schema.legalDocumentVersions.documentId, doc.id))
      .orderBy(desc(schema.legalDocumentVersions.version));
  });

export const getLegalDocumentVersion = createServerFn({ method: "GET" })
  .inputValidator(z.object({ slug: slugSchema, version: z.number().int().min(1) }))
  .handler(async ({ data }) => {
    await requirePermission("pages", "read");
    const [doc] = await getDb()
      .select({ id: schema.legalDocuments.id })
      .from(schema.legalDocuments)
      .where(eq(schema.legalDocuments.slug, data.slug))
      .limit(1);
    if (!doc) return null;

    const [version] = await getDb()
      .select()
      .from(schema.legalDocumentVersions)
      .where(
        and(
          eq(schema.legalDocumentVersions.documentId, doc.id),
          eq(schema.legalDocumentVersions.version, data.version),
        ),
      )
      .limit(1);
    return version ?? null;
  });

export const restoreLegalDocumentVersion = createServerFn({ method: "POST" })
  .inputValidator(z.object({ slug: slugSchema, version: z.number().int().min(1) }))
  .handler(async ({ data }) => {
    const user = await requirePermission("pages", "write");
    const db = getDb();
    const [doc] = await db
      .select()
      .from(schema.legalDocuments)
      .where(eq(schema.legalDocuments.slug, data.slug))
      .limit(1);
    if (!doc) throw new Error("Document not found");

    const [version] = await db
      .select()
      .from(schema.legalDocumentVersions)
      .where(
        and(
          eq(schema.legalDocumentVersions.documentId, doc.id),
          eq(schema.legalDocumentVersions.version, data.version),
        ),
      )
      .limit(1);
    if (!version) throw new Error("Version not found");

    await db
      .update(schema.legalDocuments)
      .set({
        title: version.title,
        draftContentHtml: version.contentHtml,
        metaTitle: version.metaTitle,
        metaDescription: version.metaDescription,
        updatedBy: user.id,
        updatedAt: new Date(),
      })
      .where(eq(schema.legalDocuments.id, doc.id));

    return { ok: true };
  });
